import React from 'react';
import { Card, Badge, Button } from 'react-bootstrap';
import { Eye, Trash2, Mail, Phone, Circle, CheckCircle } from 'lucide-react';
import { ContactLead } from '../../services/contactLeadService';

interface ContactLeadsListViewProps {
    leads: ContactLead[];
    canEdit: boolean;
    onView: (lead: ContactLead) => void;
    onDelete: (id: number) => void;
}

const ContactLeadsListView: React.FC<ContactLeadsListViewProps> = ({
    leads,
    canEdit,
    onView,
    onDelete,
}) => {
    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'new':
                return <Badge bg="primary"><Circle size={12} className="me-1" />New</Badge>;
            case 'read':
                return <Badge bg="info"><Eye size={12} className="me-1" />Read</Badge>;
            case 'closed':
                return <Badge bg="success"><CheckCircle size={12} className="me-1" />Closed</Badge>;
            default:
                return <Badge bg="secondary">{status}</Badge>;
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const isEmail = (contact: string) => contact.includes('@');

    const getWhatsAppLink = (phoneNumber: string) => {
        // Remove all non-numeric characters except +
        const cleanNumber = phoneNumber.replace(/[^\d+]/g, '');
        return `https://wa.me/${cleanNumber}`;
    };

    return (
        <div className="row g-3">
            {leads.map((lead) => (
                <div key={lead.id} className="col-12 col-md-6 col-lg-4">
                    <Card className="h-100 border-0 shadow-sm" style={{ transition: 'transform 0.2s, box-shadow 0.2s' }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-4px)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                        }}
                    >
                        <Card.Body>
                            <div className="d-flex justify-content-between align-items-start mb-3">
                                <div className="flex-grow-1">
                                    <small className="text-muted">{formatDate(lead.created_at)}</small>
                                </div>
                                {getStatusBadge(lead.status)}
                            </div>

                            <div className="mb-4" style={{ minHeight: '60px' }}>
                                <div className="mb-2">
                                    {isEmail(lead.contact) ? (
                                        <>
                                            <Mail size={16} className="me-2 text-muted" />
                                            <a href={`mailto:${lead.contact}`} style={{ textDecoration: 'none', color: '#111827', fontWeight: '600' }}>
                                                {lead.contact}
                                            </a>
                                        </>
                                    ) : (
                                        <>
                                            <Phone size={16} className="me-2 text-muted" />
                                            <a href={getWhatsAppLink(lead.contact)} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: '#111827', fontWeight: '600' }}>
                                                {lead.contact}
                                            </a>
                                        </>
                                    )}
                                </div>
                            </div>

                            <div className="d-flex justify-content-between align-items-center pt-3 border-top">
                                <Badge bg="secondary" className="small">{lead.source || 'unknown'}</Badge>
                                <div className="d-flex gap-2">
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={() => onView(lead)}
                                    >
                                        <Eye size={14} />
                                    </Button>
                                    {canEdit && (
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={() => onDelete(lead.id)}
                                        >
                                            <Trash2 size={14} />
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </div>
            ))}
        </div>
    );
};

export default ContactLeadsListView;
